import { useCallback, useEffect, useState } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Button,
    Tabs,
    Tab,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedIcon from '@mui/icons-material/Verified';
import DescriptionIcon from '@mui/icons-material/Description';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import b64toBlob from 'b64-to-blob';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useSearchParams } from 'react-router-dom';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchTeamMemberSummary, MenuProps, exportTeamMemberSummary } from '../../Widgets/WidgetService';
import TeamMemberSummaryModel, { IScheduledMemberNeed, IProofDocument } from '../../../Models/Widget/TeamMemberSummary.model';
import { parentUrl } from '../../../utils/constants';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import '../Widgets.css';

const TeamMemberSummaryDetail = () => {
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [summary, setSummary] = useState<TeamMemberSummaryModel>(new TeamMemberSummaryModel());
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<number>(0);

    // Proof Document Modal State
    const [proofModalOpen, setProofModalOpen] = useState<boolean>(false);
    const [selectedMemberProof, setSelectedMemberProof] = useState<{ memberName: string; proofs: IProofDocument[] } | null>(null);

    const {
        teamMemberSelectedPlans,
        setTeamMemberSelectedPlans,
        teamMemberSelectedTeam,
        setTeamMemberSelectedTeam,
        teamShowInActiveTeams,
        setTeamShowInActiveTeams,
        teamMemberFromDate,
        setTeamMemberFromDate,
        teamMemberToDate,
        setTeamMemberToDate,
    } = useWidgetContext();

    const hasElevatedFilterAccess = searchParams.get('hasElevatedFilterAccess') === 'true';
    const initialTabParam = searchParams.get('tab');

    useEffect(() => {
        dispatch(fetchTeams(true));
        if (initialTabParam) {
            const parsedTab = parseInt(initialTabParam, 10);
            if (!isNaN(parsedTab) && parsedTab >= 0 && parsedTab <= 4) {
                setActiveTab(parsedTab);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
    }, [isFetched, teamMemberSelectedPlans]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        const changeFilter = async () => {
            setIsMembersLoading(false);
            const data = await fetchTeamMemberSummary(
                teamMemberSelectedPlans.join(','),
                teamMemberSelectedTeam,
                Boolean(teamMemberFromDate) ? new Date(teamMemberFromDate).toISOString() : '',
                Boolean(teamMemberToDate) ? new Date(teamMemberToDate).toISOString() : '',
                teamShowInActiveTeams
            );
            setSummary(data || new TeamMemberSummaryModel());
            setIsMembersLoading(true);
            setIsFetched(true);
        };
        if ((teamMemberFromDate != null && teamMemberToDate != null) || (teamMemberFromDate == null && teamMemberToDate == null)) {
            changeFilter();
        }
    }, [teamMemberSelectedPlans, teamMemberSelectedTeam, teamMemberFromDate, teamMemberToDate, teamShowInActiveTeams]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportTeamMemberSummary(
            teamMemberSelectedPlans.join(','),
            teamMemberSelectedTeam,
            Boolean(teamMemberFromDate) ? new Date(teamMemberFromDate).toISOString() : '',
            Boolean(teamMemberToDate) ? new Date(teamMemberToDate).toISOString() : '',
            teamShowInActiveTeams
        );
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1;
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `TeamSchedule_MemberNeedsProof - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const handleOpenProofModal = (row: IScheduledMemberNeed) => {
        setSelectedMemberProof({
            memberName: row.memberName,
            proofs: row.proofDocuments || [],
        });
        setProofModalOpen(true);
    };

    const getActiveList = (): IScheduledMemberNeed[] => {
        switch (activeTab) {
            case 0:
                return summary.scheduledMembersOpenTcNoLicensedVisit || [];
            case 1:
                return summary.scheduledMembersInTcNotDischarged || [];
            case 2:
                return summary.scheduledMembersExpiredOrNotMetHedis || [];
            case 3:
                return summary.scheduledMembersMissingConsentOrCarePlan || [];
            case 4:
                return summary.scheduledMembersCanceledNeedingReschedule || [];
            default:
                return summary.allScheduledMembersWithNeeds || [];
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 50,
            sortable: false,
            renderCell: (params) => {
                return <Typography variant="body2">{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
        },
        {
            field: 'memberName',
            headerName: 'Scheduled Member',
            flex: 1.2,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => window.open(`${parentUrl}provider/members-list/member-chart/${params.row.memberId}/member-profile`, '_blank')}
                >
                    {params.row.memberName}
                </Typography>
            ),
        },
        {
            field: 'ahcccsId',
            headerName: 'AHCCCS ID',
            flex: 0.8,
        },
        {
            field: 'scheduledAppointmentDate',
            headerName: 'Scheduled Date (Next 7 Days)',
            flex: 1.2,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value ? new Date(params.value).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                </Typography>
            ),
        },
        {
            field: 'scheduledProvider',
            headerName: 'Scheduled Provider',
            flex: 1,
        },
        {
            field: 'healthPlan',
            headerName: 'Health Plan',
            flex: 1,
        },
        {
            field: 'needSummary',
            headerName: 'Clinical Need Summary',
            flex: 2,
            renderCell: (params) => (
                <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'proofDocuments',
            headerName: 'Document Proof',
            flex: 1,
            renderCell: (params) => (
                <Tooltip title="View Document Proof Citations">
                    <Chip
                        icon={<VerifiedIcon />}
                        label={`${params.value?.length || 0} Proof Record(s)`}
                        color="success"
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenProofModal(params.row)}
                        sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    />
                </Tooltip>
            ),
        },
    ];

    const tabCategories = [
        { label: 'Open TC - Missing Licensed Visit', count: summary.countOpenTcNoLicensedVisit || (summary.scheduledMembersOpenTcNoLicensedVisit ? summary.scheduledMembersOpenTcNoLicensedVisit.length : 0) },
        { label: 'In TC - Pending Discharge', count: summary.countInTcNotDischarged || (summary.scheduledMembersInTcNotDischarged ? summary.scheduledMembersInTcNotDischarged.length : 0) },
        { label: 'Expired / Not Met HEDIS', count: summary.countExpiredOrNotMetHedis || (summary.scheduledMembersExpiredOrNotMetHedis ? summary.scheduledMembersExpiredOrNotMetHedis.length : 0) },
        { label: 'Missing Consent / Signed Care Plan', count: summary.countMissingConsentOrCarePlan || (summary.scheduledMembersMissingConsentOrCarePlan ? summary.scheduledMembersMissingConsentOrCarePlan.length : 0) },
        { label: 'Canceled - Needs Reschedule', count: summary.countCanceledNeedingReschedule || (summary.scheduledMembersCanceledNeedingReschedule ? summary.scheduledMembersCanceledNeedingReschedule.length : 0) },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5} sx={{ p: 2, m: 1 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={12}>
                        <Box display="flex" alignItems="center">
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" onClick={backToWidget} />
                            <Typography variant="h6" color={'primary'} fontWeight="bold">
                                Team Schedule Needs & Grounded Document Proof (7-Day Scope)
                            </Typography>
                            <Chip
                                label={`Reference Date: ${new Date(summary.referenceDate || new Date()).toLocaleDateString('en-US')}`}
                                variant="outlined"
                                color="default"
                                sx={{ ml: 2, fontWeight: 'bold' }}
                            />
                            {summary.totalScheduledMembersNext7Days > 0 && (
                                <Chip
                                    label={`Total Scheduled Members (7 Days): ${summary.totalScheduledMembersNext7Days}`}
                                    color="primary"
                                    sx={{ ml: 2, fontWeight: 'bold' }}
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mt={1} alignItems="center">
                    {hasElevatedFilterAccess && (
                        <Grid size={2.5}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={teamMemberSelectedTeam}
                                onChange={(id) => setTeamMemberSelectedTeam(id)}
                                showInactive={teamShowInActiveTeams}
                                onToggleInactive={setTeamShowInActiveTeams}
                            />
                        </Grid>
                    )}
                    <Grid size={2.5}>
                        <HealthPlanMultiSelect
                            value={teamMemberSelectedPlans}
                            onChange={setTeamMemberSelectedPlans}
                            menuProps={MenuProps}
                        />
                    </Grid>
                    <Grid size={2}>
                        <DesktopDatePicker
                            label="From Date"
                            value={Boolean(teamMemberFromDate) ? teamMemberFromDate : null}
                            minDate={new Date('2017-01-01')}
                            onChange={(newValue) => setTeamMemberFromDate(newValue)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                    </Grid>
                    <Grid size={2}>
                        <DesktopDatePicker
                            label="To Date"
                            value={Boolean(teamMemberToDate) ? teamMemberToDate : null}
                            minDate={new Date('2017-01-01')}
                            onChange={(newValue) => setTeamMemberToDate(newValue)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                    </Grid>
                    <Grid size={hasElevatedFilterAccess ? 3 : 5.5} textAlign="right">
                        <Button onClick={exportDetail} variant="contained" color="success" sx={{ minWidth: '140px' }}>
                            {downLoading ? 'Exporting...' : 'Export Excel with Proof'}
                        </Button>
                    </Grid>
                </Grid>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} variant="scrollable" scrollButtons="auto">
                        {tabCategories.map((cat, idx) => (
                            <Tab
                                key={idx}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span>{cat.label}</span>
                                        <Chip label={cat.count} size="small" color={cat.count > 0 ? 'error' : 'default'} />
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                <Box mt={2} sx={{ height: '62vh', width: '100%', position: 'relative' }}>
                    {!isMembersLoading ? (
                        <Box className="loader-center">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DataGrid
                            columns={columns}
                            rows={getActiveList()}
                            getRowId={(row) => `${row.memberId}-${row.appointmentId || row.scheduledAppointmentDate}`}
                            paginationMode="client"
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            }}
                            density="comfortable"
                        />
                    )}
                </Box>

                {/* Proof Document Modal Dialog */}
                <Dialog open={proofModalOpen} onClose={() => setProofModalOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VerifiedIcon color="success" />
                        Zero-Hallucination Document Proof Citations ({selectedMemberProof?.memberName})
                    </DialogTitle>
                    <DialogContent dividers>
                        {selectedMemberProof?.proofs.length === 0 ? (
                            <Typography color="textSecondary">No backing proof documents attached.</Typography>
                        ) : (
                            selectedMemberProof?.proofs.map((proof, i) => (
                                <Paper key={i} elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#f9fbe7', borderLeft: '4px solid #4caf50' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                            [{proof.sourceRecordType}] {proof.title}
                                        </Typography>
                                        <Chip label={proof.status || 'Verified'} size="small" color="primary" variant="outlined" />
                                    </Box>
                                    {proof.documentDate && (
                                        <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                            Record Date: {new Date(proof.documentDate).toLocaleDateString()} | Record ID: {proof.recordId}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', backgroundColor: '#ffffff', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                        "{proof.proofExcerpt}"
                                    </Typography>
                                </Paper>
                            ))
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setProofModalOpen(false)} color="primary" variant="contained">
                            Close Proof Window
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </LocalizationProvider>
    );
};

export default TeamMemberSummaryDetail;
